import { Link } from "react-router-dom";
import { 
  Activity,
  Brain,
  Heart,
  Hand,
  Ear,
  Eye,
  Footprints,
  Dumbbell,
  Waves,
  Wind,
  Sparkles,
  Users,
  Clock,
  Award,
  CheckCircle2,
  Phone,
  ArrowRight,
  Zap,
  PersonStanding,
  Accessibility
} from "lucide-react";
import { Button } from "@/components/ui/button";

const therapyServices = [
  {
    icon: Activity,
    title: "Physical Therapy",
    description: "Restore movement and function after injury, surgery, or illness. Our licensed physical therapists use evidence-based techniques to improve mobility, reduce pain, and enhance quality of life.",
    treatments: ["Post-surgical rehabilitation", "Sports injury recovery", "Balance training", "Gait analysis", "Strength conditioning"],
    color: "#00f0ff"
  },
  {
    icon: Brain,
    title: "Occupational Therapy",
    description: "Regain independence in daily activities. Our occupational therapists help patients develop, recover, and maintain skills needed for daily living and working.",
    treatments: ["Activities of daily living (ADL)", "Cognitive rehabilitation", "Fine motor skills", "Adaptive equipment training", "Home safety assessment"],
    color: "#ff00ff"
  },
  {
    icon: Ear,
    title: "Speech-Language Therapy",
    description: "Improve communication and swallowing abilities. Our speech-language pathologists treat disorders affecting speech, language, voice, and swallowing.",
    treatments: ["Aphasia treatment", "Dysarthria therapy", "Dysphagia management", "Voice therapy", "Cognitive-communication therapy"],
    color: "#ff9e00"
  },
  {
    icon: Heart,
    title: "Cardiac Rehabilitation",
    description: "Strengthen your heart and improve cardiovascular health after cardiac events. Supervised exercise and education programs tailored to your needs.",
    treatments: ["Post-heart attack recovery", "Heart surgery rehabilitation", "Heart failure management", "Cardiovascular conditioning", "Lifestyle modification"],
    color: "#00f0ff"
  },
  {
    icon: Wind,
    title: "Pulmonary Rehabilitation",
    description: "Breathe easier with our comprehensive pulmonary therapy programs. We help patients with chronic lung conditions improve their breathing and quality of life.",
    treatments: ["COPD management", "Breathing exercises", "Oxygen therapy training", "Energy conservation", "Airway clearance techniques"],
    color: "#ff00ff"
  },
  {
    icon: Brain,
    title: "Neurological Rehabilitation",
    description: "Recover function after stroke, brain injury, or neurological conditions. Our specialized team uses the latest techniques for neurological recovery.",
    treatments: ["Stroke recovery", "Traumatic brain injury", "Parkinson's disease", "Multiple sclerosis", "Spinal cord injury"],
    color: "#ff9e00"
  },
  {
    icon: Dumbbell,
    title: "Orthopedic Rehabilitation",
    description: "Recover from bone, joint, and muscle injuries or surgeries. Comprehensive care for orthopedic conditions to restore strength and mobility.",
    treatments: ["Joint replacement rehab", "Fracture recovery", "Arthritis management", "Back and neck pain", "Sports medicine"],
    color: "#00f0ff"
  },
  {
    icon: Waves,
    title: "Aquatic Therapy",
    description: "Gentle, effective rehabilitation in our heated therapy pool. Water-based exercises reduce joint stress while building strength and flexibility.",
    treatments: ["Low-impact exercise", "Arthritis relief", "Post-surgical recovery", "Pain management", "Balance improvement"],
    color: "#ff00ff"
  },
  {
    icon: Hand,
    title: "Hand Therapy",
    description: "Specialized rehabilitation for hand, wrist, and arm conditions. Our certified hand therapists help restore function after injury or surgery.",
    treatments: ["Carpal tunnel syndrome", "Trigger finger", "Fracture recovery", "Tendon repair", "Arthritis management"],
    color: "#ff9e00"
  },
  {
    icon: PersonStanding,
    title: "Vestibular Therapy",
    description: "Treat dizziness, vertigo, and balance disorders. Our vestibular rehabilitation program helps patients regain stability and reduce fall risk.",
    treatments: ["BPPV treatment", "Balance retraining", "Gaze stabilization", "Habituation exercises", "Fall prevention"],
    color: "#00f0ff"
  },
  {
    icon: Zap,
    title: "Pain Management Therapy",
    description: "Comprehensive approach to managing chronic and acute pain. Our team uses multiple modalities to reduce pain and improve function.",
    treatments: ["Manual therapy", "Electrical stimulation", "Ultrasound therapy", "Heat/cold therapy", "Dry needling"],
    color: "#ff00ff"
  },
  {
    icon: Accessibility,
    title: "Geriatric Rehabilitation",
    description: "Specialized therapy for older adults. We address age-related conditions and help seniors maintain independence and quality of life.",
    treatments: ["Fall prevention", "Mobility improvement", "Strength building", "Cognitive exercises", "Chronic disease management"],
    color: "#ff9e00"
  }
];

const equipment = [
  "State-of-the-Art Therapy Gym",
  "Heated Aquatic Therapy Pool",
  "Advanced Electrical Stimulation Units",
  "Ultrasound & Laser Therapy",
  "Parallel Bars & Gait Training",
  "Balance Assessment Technology",
  "Functional Capacity Evaluation",
  "Hand Therapy Workstation",
  "Cardiovascular Equipment",
  "Resistance Training Machines"
];

export default function TherapyPage() {
  return (
    <div data-testid="therapy-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-[#ff00ff] text-sm font-mono uppercase tracking-widest mb-6">
              Rehabilitation Services
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Physical Therapy & <span className="text-gradient">Rehabilitation</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Comprehensive therapy services to help you recover, rebuild, and return to the activities you love. 
              Our expert team provides personalized care using state-of-the-art equipment.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:8449436175">
                <Button 
                  data-testid="therapy-call-btn"
                  className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule Evaluation
                </Button>
              </a>
              <Link to="/contact">
                <Button 
                  data-testid="therapy-contact-btn"
                  variant="outline"
                  className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#ff00ff] hover:text-white transition-all"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#1a0b2e]/30 border-y border-[#4b0082]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "12+", label: "Therapy Types", color: "#00f0ff" },
              { value: "25+", label: "Licensed Therapists", color: "#ff00ff" },
              { value: "98%", label: "Patient Satisfaction", color: "#ff9e00" },
              { value: "24/7", label: "Inpatient Services", color: "#00f0ff" },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Therapy Services */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Our Services</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Comprehensive Therapy Services
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From physical therapy to specialized rehabilitation programs, we offer a full spectrum of services to meet your recovery needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {therapyServices.map((service, index) => (
              <div
                key={index}
                data-testid={`therapy-service-${index}`}
                className="glass rounded-2xl p-6 card-hover group"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color }} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                
                <div className="space-y-2">
                  {service.treatments.slice(0, 3).map((treatment, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#00f0ff] shrink-0" />
                      <span className="text-gray-300">{treatment}</span>
                    </div>
                  ))}
                  {service.treatments.length > 3 && (
                    <span className="text-[#ff00ff] text-sm">+{service.treatments.length - 3} more treatments</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment & Facilities */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Our Facility</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                State-of-the-Art Equipment
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Our rehabilitation center features the latest technology and equipment to ensure the most effective treatment outcomes. 
                From our heated aquatic therapy pool to advanced diagnostic tools, we have everything needed for comprehensive rehabilitation.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {equipment.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00f0ff] shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400"
                  alt="Physical therapy equipment"
                  className="rounded-2xl w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400"
                  alt="Therapy session"
                  className="rounded-2xl w-full h-64 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1519824145371-296894a0daa9?w=400"
                  alt="Rehabilitation exercises"
                  className="rounded-2xl w-full h-64 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1597452485669-2c7bb5fef90d?w=400"
                  alt="Aquatic therapy pool"
                  className="rounded-2xl w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Our Approach</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              How We Help You Recover
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Evaluation", desc: "Comprehensive assessment of your condition, goals, and functional abilities", color: "#00f0ff" },
              { step: "02", title: "Treatment Plan", desc: "Personalized therapy program designed specifically for your needs", color: "#ff00ff" },
              { step: "03", title: "Active Therapy", desc: "Regular sessions with our licensed therapists using proven techniques", color: "#ff9e00" },
              { step: "04", title: "Progress & Goals", desc: "Continuous monitoring and adjustment to achieve optimal outcomes", color: "#00f0ff" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-6xl font-bold mb-4 opacity-30"
                  style={{ color: item.color }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance & Coverage */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Coverage</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Insurance & Payment
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We accept most major insurance plans and work with you to maximize your benefits
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              "Medicare",
              "Medicaid",
              "Blue Cross Blue Shield",
              "Aetna",
              "UnitedHealthcare",
              "Cigna",
              "Humana",
              "Workers' Compensation",
              "Auto Insurance (PIP)",
              "Private Pay"
            ].map((insurance, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-gray-300"
              >
                {insurance}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Recovery Journey Today
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Our experienced therapy team is ready to help you achieve your rehabilitation goals. 
            Schedule your evaluation and take the first step toward recovery.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:8449436175">
              <Button 
                data-testid="therapy-cta-call"
                className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 844-943-6175
              </Button>
            </a>
            <Link to="/appointment">
              <Button 
                data-testid="therapy-cta-appointment"
                variant="outline"
                className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#ff00ff] hover:text-white transition-all"
              >
                Book Appointment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
